import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import PageHero from '@/components/shared/PageHero';
import SectionHeader from '@/components/shared/SectionHeader';
import { publicApi } from '@/lib/api';
import useSEO from '@/hooks/useSEO';
import { CSR_TIERS } from '@/pages/csr/csrConfig';
import TierPDFCard from '@/pages/csr/TierPDFCard';

export default function CSRProjectProposalsPage() {
    const [pdfs, setPdfs] = useState([]);

    useSEO({
        title: 'Project Proposals',
        description:
            'Downloadable CSR project proposals from VVVSI Trust, grouped by sector — Education, Health, Skill Development, Green Visionary, Employment, Disaster and Others.',
    });

    useEffect(() => {
        publicApi.getCsrProjectPdfs().then(setPdfs).catch(() => {});
    }, []);

    const pdfsByTier = CSR_TIERS.reduce((acc, t) => {
        acc[t.key] = pdfs.filter((p) => p.tier === t.key);
        return acc;
    }, {});

    return (
        <div data-testid="csr-project-proposals-page">
            <PageHero
                eyebrow="CSR Projects"
                title="Project Proposals"
                subtitle="Downloadable project decks for CSR partners — grouped by sector so teams can quickly find programs that fit their mandate."
            />

            <section className="section-pad">
                <div className="container-page">
                    <SectionHeader
                        eyebrow="Project Proposals"
                        title="Sector-wise project decks."
                        description="Grouped by sector so CSR teams can quickly identify programs that fit their focus."
                    />

                    <div
                        className="mt-8 space-y-10"
                        data-testid="csr-project-pdfs-section"
                    >
                        {CSR_TIERS.map((tier) => {
                            const list = pdfsByTier[tier.key] || [];
                            const Icon = tier.icon;
                            return (
                                <div key={tier.key} data-testid={`pdf-tier-${tier.key}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-11 w-11 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(30_90%_28%)] flex items-center justify-center">
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))]">
                                                    {tier.label}
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    {tier.blurb}
                                                </div>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="self-start">
                                            {list.length}{' '}
                                            {list.length === 1 ? 'proposal' : 'proposals'}
                                        </Badge>
                                    </div>
                                    {list.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {list.map((pdf) => (
                                                <TierPDFCard key={pdf.id} pdf={pdf} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                                            New {tier.label.toLowerCase()} proposals coming soon.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}
